import {
  ChangeDetectionStrategy,
  Component,
  type Signal,
  computed,
  inject,
  input,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  CalendlyBookingDialogComponent,
  DialogPresets,
  NgCommonModule,
  injectStylesheet,
  type ICalendlyBookingDialogData,
  type ICalendlyBookingDialogResult,
} from '@principle-theorem/ng-shared';
import { type IPractice } from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  toTimestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { AI_SUITE_CONFIG } from '../../../ai-suite-config';

@Component({
  selector: 'pr-ai-receptionist-landing',
  imports: [NgCommonModule],
  templateUrl: './ai-receptionist-landing.component.html',
  styleUrls: ['./ai-receptionist-landing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiReceptionistLandingComponent {
  private readonly _config = inject(AI_SUITE_CONFIG);
  private readonly _dialog = inject(BasicDialogService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _organisationService = inject(OrganisationService);
  private readonly _user = toSignal(this._organisationService.user$);
  private readonly _organisation = toSignal(
    this._organisationService.organisation$
  );

  readonly practice = input.required<WithRef<IPractice>>();
  readonly isBooked: Signal<boolean> = computed(
    () => !!this.practice().settings?.aiReceptionist?.demoBooking?.requestedAt
  );

  constructor() {
    injectStylesheet(
      'ai-receptionist-landing-fonts',
      'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Poppins:wght@500;600;700;800&display=swap'
    );
  }

  async openBookingDialog(): Promise<void> {
    const url = this._config.demoBookingUrl;
    if (!url) {
      return;
    }

    const practice = this.practice();
    const user = this._user();
    const organisation = this._organisation();

    const data: ICalendlyBookingDialogData = {
      url,
      title: 'Book Your AI Receptionist Demo',
      hideGdprBanner: true,
      prefill: {
        name: user?.name,
        email: user?.email,
        customAnswers: {
          a1: practice.name,
          a2: organisation?.name ?? '',
        },
      },
    };

    const result = await this._dialog
      .open<
        CalendlyBookingDialogComponent,
        ICalendlyBookingDialogData,
        ICalendlyBookingDialogResult
      >(
        CalendlyBookingDialogComponent,
        DialogPresets.sideSheet<ICalendlyBookingDialogData>({ data })
      )
      .afterClosed()
      .toPromise();

    if (result?.scheduled === true) {
      await this._persistBooking(result);
    }
  }

  private async _persistBooking(
    result: ICalendlyBookingDialogResult
  ): Promise<void> {
    const practice = this.practice();
    try {
      await Firestore.patchDoc(practice.ref, {
        settings: {
          ...practice.settings,
          aiReceptionist: {
            ...practice.settings?.aiReceptionist,
            enabled: practice.settings?.aiReceptionist?.enabled ?? false,
            demoBooking: {
              requestedAt: toTimestamp(),
              eventUri: result.eventUri,
              inviteeUri: result.inviteeUri,
            },
          },
        },
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to persist AI Receptionist demo booking', error);
      this._snackBar.open(
        'Your demo is booked, but we could not update this page. Please refresh to see it.',
        'Dismiss',
        { duration: 6000 }
      );
    }
  }
}
